/**
 * 휴대폰 화면 보강(표시만, 업무 데이터는 읽거나 바꾸지 않는다):
 * - 단순한 표는 열 이름을 칸에 붙여 카드처럼 보이게(data-mobile-cards, CSS 는 design-v3.css)
 * - 가로로 넓은 시간표에 "좌우로 밀기" 한 줄(수납 비교표는 표 위 좌우 이동 도구가 따로 있다)
 * - "맨 위로" 버튼, 화면 키보드가 열리면 아래 메뉴 숨김, 화면을 옮기면 맨 위로·본문에 초점
 */
export function startEnhancements(root: HTMLElement) {
  const top = document.createElement('button');
  top.type = 'button'; top.className = 'mobile-back-top'; top.hidden = true;
  top.textContent = '↑ 맨 위'; top.setAttribute('aria-label', '화면 맨 위로 이동');
  top.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth' });
    document.getElementById('main')?.focus({ preventScroll: true });
  });
  document.body.append(top);
  let scrollQueued = false;
  window.addEventListener('scroll', () => {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(() => { top.hidden = window.scrollY < 480; scrollQueued = false; });
  }, { passive: true });

  let pending = false;
  const enhance = () => {
    pending = false;
    for (const table of root.querySelectorAll<HTMLTableElement>('.table:not(.bs-table):not(.bs-pay)')) {
      const heads = [...table.querySelectorAll<HTMLTableRowElement>(':scope > thead > tr')];
      const rows = [...table.querySelectorAll<HTMLTableRowElement>(':scope > tbody > tr')];
      if (heads.length !== 1 || !rows.length) continue;
      const headers = [...heads[0]!.children] as HTMLTableCellElement[];
      const simple = headers.length >= 2 && headers.every((c) => c.colSpan === 1 && c.rowSpan === 1)
        && rows.every((r) => r.children.length === headers.length && [...r.children].every((c) => c.tagName === 'TD' && (c as HTMLTableCellElement).colSpan === 1 && (c as HTMLTableCellElement).rowSpan === 1));
      if (!simple) { table.removeAttribute('data-mobile-cards'); continue; }
      table.setAttribute('data-mobile-cards', '');
      rows.forEach((r) => [...r.children].forEach((cell, i) => {
        (cell as HTMLElement).dataset.mobileLabel = headers[i]!.textContent?.replace(/\s+/g, ' ').trim() || '작업';
      }));
    }
    // 표가 사라졌는데 남은 안내 줄(보기를 바꾼 경우)은 지운다
    for (const note of root.querySelectorAll('.mobile-scroll-note')) {
      const next = note.nextElementSibling;
      if (!next || !next.matches('.tg')) note.remove();
    }
    for (const wrap of root.querySelectorAll<HTMLElement>('.tg')) {
      if (!wrap.previousElementSibling?.classList.contains('mobile-scroll-note')) {
        const note = document.createElement('p');
        note.className = 'mobile-scroll-note';
        note.textContent = '좌우로 밀어 보기';
        wrap.before(note);
      }
      if (!wrap.hasAttribute('tabindex')) wrap.tabIndex = 0;
      if (!wrap.hasAttribute('aria-label')) wrap.setAttribute('aria-label', '일정 시간표, 좌우 스크롤');
    }
  };
  const queue = () => { if (!pending) { pending = true; requestAnimationFrame(enhance); } };
  new MutationObserver(queue).observe(root, { childList: true, subtree: true });
  queue();

  const viewport = () => {
    const vv = window.visualViewport;
    const active = document.activeElement;
    const editing = active?.matches('input:not([type=checkbox]):not([type=radio]),textarea,select');
    document.body.classList.toggle('mobile-keyboard', Boolean(vv && editing && window.innerHeight - vv.height > 120));
    if (vv) document.documentElement.style.setProperty('--mobile-viewport-height', `${Math.round(vv.height)}px`);
  };
  window.visualViewport?.addEventListener('resize', viewport, { passive: true });
  window.addEventListener('resize', viewport, { passive: true });
  document.addEventListener('focusin', viewport);
  document.addEventListener('focusout', () => requestAnimationFrame(viewport));
  viewport();

  window.addEventListener('hashchange', () => requestAnimationFrame(() => {
    if (document.querySelector('dialog[open]')) return;
    document.getElementById('main')?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }));
}
